import { SelectTrigger } from '../Layout';
import { API, Application, UnitsSystem, UserProfiles } from 'libapi';
import React, {
	ChangeEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

export function to_axis_string(i: number) {
	return 'XYZθ'[i];
}

export interface ControlProps {
	handleBack: () => void;
	handleNext: () => void;
	instructionNumber: number;
}
// ** _master.scss
// $medium-blue: rgb(43, 62, 85);
// $container-blue: rgb(50, 72, 102);
// $dark-blue: rgb(22, 35, 56);
// $light-grey: rgb(212, 217, 226);
// $dull-button: rgb(167, 176, 187);
// $yellow: #ffd723;
// $sky-blue: rgb(29, 154, 245);
// $green: rgb(91, 196, 126);
// $light-blue: rgb(239, 242, 247);
// $dark-grey: rgb(135, 135, 135);
// $desert-storm: rgb(248, 248, 248);
// $off-white: rgb(251, 251, 251);
// $red: rgb(243, 15, 15);
// $border-grey: rgb(207, 207, 207);

export enum COLOR {
	BOX = 'rgb(89,69,50)', // 0x594532
	CLEAR_BOX = 'rgba(89,69,50,0.9)',
	SELECTED_BOX = 'rgba(195,129,66, 0.9)',
	RED_CLEAR = 'rgba(243, 15, 15, 0.8)',
	MOVE_BOX = 'rgba(195,129,66, 0.9)',
	LOG = '#D2AB6F',
	PLANK = '#E6BF83',
	DULL_PLANK = '#D4C5AD',
	DARK_PLANK = 'rgb(230, 191,131)',
	YELLOW_METAL = '#725932',
	CARDBOARD = '#AD8762',
	MEDIUM_BLUE = '#192f47',
	CONTAINER_BLUE = '#192f47',
	DESERT_STORM = 'rgb(248, 248, 248)',
	LIGHT_BLUE = '#337BD1',
	DARK_BLUE = '#1C344F',
	GREEN = 'rgb(91, 196, 126)',
	TRANSPARENT_GREEN = 'rgba(91, 196, 126, 0.2)',
	RED = 'rgb(243, 15, 15)',
	DARK_GREY = 'rgb(135, 135, 135)',
	OFF_WHITE = 'rgb(251, 251, 251)',
	WHITE = 'rgb(255,255,255)',
	YELLOW = '#ffd723',
	SKY_BLUE = 'rgb(29, 154, 245)',
	BORDER_GREY = 'rgb(207, 207, 207)',
	TRANSPARENT = 'rgba(0,0,0,0)',
	ELECTRIC_BLUE = '#0080F9',

	SILVER = '#eff2f7',
	SLIPSHEET_YELLOW = '#F4B73A',
	BLACK = '#000000',
}

/* Units utilities (limited to lengths at the moment) */
export enum UNITS {
	millimeters = 1,
	inches = 0.0393701,
	feet = 0.00328084,
}

/* extra precision */
export const renderInputLength = function (
	pValue: number,
	pUnitsSystem: UnitsSystem
) {
	return render_length(pValue, pUnitsSystem, false, 4);
};

export const renderInputWeight = function (
	pValue: number,
	pUnitsSystem: UnitsSystem
) {
	return render_weight(pValue, pUnitsSystem, false, 4);
};

export const to_unit = (v: number, u: UNITS): number => v * u;
export const from_unit = (v: number, u: UNITS): number => v / u;
export const unit_string = (u: UNITS) => {
	switch (u) {
		case UNITS.millimeters:
			return 'mm';
		case UNITS.inches:
			return 'in';
		case UNITS.feet:
			return 'ft';
		default:
			return 'mm';
	}
};

/* Standardized display units (e.g. mm<->in, kg<->lbs). */
const formattedPrecision = (pNumber: number, pPrecision: number) =>
	String(parseFloat(pNumber.toFixed(pPrecision)));

export const render_length_unit = (us: UnitsSystem) => {
	return unit_string(
		UnitsSystem.METRIC == us ? UNITS.millimeters : UNITS.inches
	);
};
export const render_length = (
	v_mm: number,
	us: UnitsSystem,
	show_unit: boolean = false,
	precision: number = 1
): string => {
	if (undefined == v_mm) {
		v_mm = 0;
	}
	return (
		formattedPrecision(
			UnitsSystem.METRIC == us ? v_mm : to_unit(v_mm, UNITS.inches),
			precision
		) + (show_unit ? ` ${render_length_unit(us)}` : '')
	);
};
export const render_weight_unit = (us: UnitsSystem) => {
	return UnitsSystem.METRIC == us ? 'kg' : 'lbs';
};
export const render_weight = (
	v_kg: number,
	us: UnitsSystem,
	show_unit: boolean = false,
	precision: number = 2
): string => {
	return (
		formattedPrecision(
			UnitsSystem.METRIC == us ? v_kg : v_kg * 2.20462,
			precision
		) + (show_unit ? ` ${render_weight_unit(us)}` : '')
	);
};

export function changeEventToNumber(e: ChangeEvent): number {
	let val: number = +(e.target as any).value;
	return val;
}

export function changeEventToString(e: ChangeEvent): string {
	let val: string = (e.target as any).value;
	return String(val);
}

export function wrapChangeEventString(
	fn: (s: string) => void
): (e: ChangeEvent) => void {
	return (e: ChangeEvent) => {
		fn(changeEventToString(e));
	};
}

export function wrapChangeEventNumber(
	fn: (n: number) => void
): (e: ChangeEvent) => void {
	return (e: ChangeEvent) => {
		fn(changeEventToNumber(e));
	};
}

export interface NumberHandlers {
	Direct: (pNewNumber: number) => void;
	Event: (e: ChangeEvent) => void;
	FromString: (pNewNumberStr: string) => void;
}

export function getChangeWrapperNumber(
	pHandler: (pNewNumber: number) => void
): NumberHandlers {
	return {
		Direct: pHandler,
		Event: wrapChangeEventNumber(pHandler),
		FromString: function (pNewNumberStr: string) {
			const lNumVal: number = +pNewNumberStr;
			if (isNaN(lNumVal)) {
				console.log('Invalid numerical input.');
				return;
			}
			pHandler(lNumVal);
		},
	};
}

export interface StringHandlers {
	Direct: (pNewStr: string) => void;
	Event: (e: ChangeEvent) => void;
}

export function getChangeWrapperString(
	pHandler: (pNewStr: string) => void
): StringHandlers {
	return {
		Direct: pHandler,
		Event: wrapChangeEventString(pHandler),
	};
}

// export const isPendant = (): boolean => {
// 	return (
// 		'ontouchstart' in window ||
// 		navigator.maxTouchPoints > 0 ||
// 		navigator.msMaxTouchPoints > 0
// 	);
// };

export type UseStateObject<T> = { Current: T; Set: (pToSet: T) => void };
export type ConfigSetter<T> = (pToSet: T) => void;
export function roundNumber(pNum: number, pPower: number = 1): number {
	const lTen = Math.pow(10, pPower);
	return Math.round(pNum * lTen) / lTen;
}
export function useContainerSize<T extends HTMLElement>() {
	const _lInternalRef = useRef<T | null>(null),
		[lDomRect, lSetDomRect] = useState<DOMRect | undefined>(void 0),
		lHandleRef = useCallback(function (pRef) {
			_lInternalRef.current = pRef;
			lSetDomRect(pRef?.getBoundingClientRect());
		}, []);
	useEffect(function () {
		const lHandleResize = function () {
			lSetDomRect(_lInternalRef.current?.getBoundingClientRect());
		};
		window.addEventListener('resize', lHandleResize);
		return function () {
			window.removeEventListener('resize', lHandleResize);
		};
	}, []);
	return { ref: lHandleRef, rect: lDomRect, internalRef: _lInternalRef };
}

export function AppFactory(): Application {
	const PORT = 9001,
		SERVER_IP = window.location.hostname,
		SERVER_PORT: number = +window.location.port,
		USE_WS = true;
	return new Application(SERVER_IP, PORT, SERVER_PORT, USE_WS);
}

export function ApiFactory(): API {
	const SERVER_IP = window.location.hostname,
		SERVER_PORT: number = +window.location.port;
	return new API(SERVER_IP, SERVER_PORT);
}

export function sleep(pTime: number) {
	return new Promise<number>(function (pResolve, _pReject) {
		setTimeout(function () {
			pResolve(pTime);
		}, pTime);
	});
}

export function startCase(pString: string) {
	return pString.charAt(0).toUpperCase() + pString.slice(1);
}

export function isWebDeployment() {
	const lResult = window.location.hostname.match(/palletizer\.vention\.(io|com)/);
	// const lResult = window.location.hostname.match(/localhost/);
	return undefined != lResult;
}

export const onSelectedUserChanged = (
	preferences: { users: Map<string, UserProfiles> },
	setSelectedUser: React.Dispatch<React.SetStateAction<UserProfiles>>,
	setSavedPassword: React.Dispatch<React.SetStateAction<string | undefined>>,
	setOldPasswordInput?: React.Dispatch<React.SetStateAction<string | undefined>>,
	setPasswordInput?: React.Dispatch<React.SetStateAction<string | undefined>>,
	setConfirmedPasswordInput?: React.Dispatch<React.SetStateAction<string | undefined>>,
) =>
	wrapChangeEventString((selectedUserName: string) => {
		const user = preferences.users.get(selectedUserName);
		if (!user) {
			console.error('User not found');
			return;
		}
		setSelectedUser(user);
		setSavedPassword(user.password);
		if (setOldPasswordInput) {
			setOldPasswordInput(undefined);
		}
		if (setPasswordInput) {
			setPasswordInput('');
		}
		if (setConfirmedPasswordInput) {
			setConfirmedPasswordInput('');
		}
	});

/**
 * Rounds the given value to the nearest decimal precision
 * @param value The number to round
 * @param precision (optional) The decimal place to round to
 * @returns The rounded number
 */
export const roundToNearestDecimal = (value: number, precision = 3): number => {
  const modifier = Math.pow(10, precision);
  return Math.round(value * modifier) / modifier;
}

